import React, { useEffect } from 'react';
import useWebSocket from 'react-use-websocket';
import { cloneDeep } from 'lodash';
import { LiveDataContext } from 'context/liveData';
import { substringAfterOccurance } from 'utils/commonFunctions';
import EventTagMapping from './mapping';
import { EventsData } from './interface';

/** Populates Realtime data for live view to use.
 * */
const PopulateLiveViewData = () => {
  // Live data with derived states.
  const { liveData, setLiveData, setIsUnauthorized, setSubscribedAgents } =
    LiveDataContext();
  const { lastJsonMessage } = useWebSocket(
    process.env.REACT_APP_WS_URL as string,
    {
      share: true,
      queryParams: {
        isSupervisor: true,
        accessToken: `${localStorage.getItem('access_token')}`,
      } as any,
      shouldReconnect: (closeEvent) => {
        if (closeEvent.code === 4001) setIsUnauthorized(true);
        if (closeEvent.code === 4001 || closeEvent.code === 4002) return false;
        return true;
      },
      reconnectAttempts: 100,
      reconnectInterval: 3000,
      retryOnError: true,
    },
  );

  /** Generates initial data with injected keys for callStatus as '-' for default
   * Generates some Warm up data for initial load.
   *  */
  const generateInitialData = (data: EventsData[], extraKeys: string[]) => {
    const flattenObj: any = {};
    const allAgents: string[] = [];
    extraKeys.forEach((key: string) => {
      flattenObj[key] = '-';
    });
    const collection = data.map((itm: EventsData) => {
      allAgents.push(substringAfterOccurance(`${itm?.agentLoginId}`, '-'));
      return {
        ...itm,
        ...flattenObj,
        lastUpdatedCallStatus: new Date(),
        lastUpdatedProNotesStatus: new Date(),
      };
    });
    setSubscribedAgents(allAgents);
    return collection;
  };

  const callStatusTimeUpdate = (agent: EventsData, statusToCheck: string) => {
    return agent.callStatus !== statusToCheck
      ? new Date()
      : agent.lastUpdatedCallStatus;
  };

  const proNotesStatusTimeUpdate = (
    agent: EventsData,
    statusToCheck: string,
  ) => {
    return agent.status !== statusToCheck
      ? new Date()
      : agent.lastUpdatedProNotesStatus;
  };

  const performChecksForCallStatus = (
    agentInfo: EventsData,
    callId: string,
    eventType: string,
  ) => {
    if (agentInfo.status === 'OFFLINE')
      return {
        ...agentInfo,
        callStatus: '-',
        status: 'AGENT_AWAY',
        callId: undefined,
        lastUpdatedCallStatus: callStatusTimeUpdate(agentInfo, '-'),
        lastUpdatedProNotesStatus: proNotesStatusTimeUpdate(
          agentInfo,
          'AGENT_AWAY',
        ),
      };
    if (agentInfo.status === 'AGENT_AWAY') return agentInfo;

    if (callId) {
      if (eventType === 'IN_CALL')
        return {
          ...agentInfo,
          callId,
          callStatus: eventType,
          lastUpdatedCallStatus: callStatusTimeUpdate(agentInfo, eventType),
        };

      if (
        eventType === 'CALL_START_WAITING' &&
        agentInfo.callId === callId &&
        agentInfo.callStatus !== 'IN_CALL'
      ) {
        return {
          ...agentInfo,
          callId: undefined,
          callStatus: eventType,
          lastUpdatedCallStatus: callStatusTimeUpdate(agentInfo, eventType),
        };
      }

      if (agentInfo.callId === callId && eventType !== 'CALL_START_WAITING')
        return {
          ...agentInfo,
          callStatus: eventType,
          lastUpdatedCallStatus: callStatusTimeUpdate(agentInfo, eventType),
        };

      if (!agentInfo.callId)
        return {
          ...agentInfo,
          callStatus: eventType,
          callId,
          lastUpdatedCallStatus: callStatusTimeUpdate(agentInfo, eventType),
        };

      return agentInfo;
    }
    return agentInfo;
  };

  const performChecksForStatus = (agentInfo: EventsData, eventType: string) => {
    if (eventType === 'OFFLINE')
      return {
        ...agentInfo,
        status: eventType,
        callStatus: '-',
        lastUpdatedProNotesStatus: proNotesStatusTimeUpdate(
          agentInfo,
          eventType,
        ),
        lastUpdatedCallStatus: callStatusTimeUpdate(agentInfo, '-'),
      };
    return {
      ...agentInfo,
      status: eventType,
      lastUpdatedProNotesStatus: proNotesStatusTimeUpdate(agentInfo, eventType),
    };
  };

  // Updates callStatus or status from live data.
  const modifyData = (
    data: EventsData[],
    agentLoginId: string,
    callId: string,
    key: string,
    value: string,
  ) => {
    if (!agentLoginId) return data;
    return data.map((itm: EventsData) => {
      if (itm.agentLoginId === agentLoginId) {
        switch (key) {
          case 'callStatus':
            return performChecksForCallStatus(itm, callId, value);
          case 'status':
            return performChecksForStatus(itm, value);
          default:
            return itm;
        }
      }
      return itm;
    });
  };

  useEffect(() => {
    if (lastJsonMessage) {
      const data = cloneDeep(lastJsonMessage);

      if (data.type === 'INITIAL_AGENT_STATUS') {
        const initialData = generateInitialData(data?.payload || [], [
          'callStatus',
        ]);
        setLiveData(initialData);
      }
      if (EventTagMapping[data.type]) {
        const modified = modifyData(
          liveData,
          data?.agentLoginId,
          data?.callId || data?.payload?.call_id || '', // Can recieve callId directly or via payload.
          EventTagMapping[data.type].type,
          EventTagMapping[data.type].map,
        );
        setLiveData(modified);
      }
    }
  }, [lastJsonMessage]);

  return <></>;
};

export default PopulateLiveViewData;
